import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import Cookies from 'js-cookie';
import CookieConsentBanner from './CookieConsentBanner';
import {
  COOKIE_CONSENT_AUDIT_STORAGE_KEY,
  COOKIE_CONSENT_STORAGE_KEY,
  getConsentDurationDays
} from './config';

export type CookieConsentPreferences = {
  necessary: true;
  analytics: boolean;
};

export type CookieConsentType = 'accept_all' | 'reject_all' | 'custom';

export type CookieConsentRecord = {
  version: number;
  status: 'accepted' | 'rejected' | 'customized';
  consentType: CookieConsentType;
  preferences: CookieConsentPreferences;
  timestamp: string;
  expiresAt: string;
  region: 'global';
};

type CookieConsentContextValue = {
  consent: CookieConsentRecord | null;
  preferences: CookieConsentPreferences;
  shouldShowBanner: boolean;
  shouldShowConsentControls: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: Omit<CookieConsentPreferences, 'necessary'>) => void;
  hasConsentFor: (category: keyof CookieConsentPreferences) => boolean;
  getAuditLog: () => CookieConsentRecord[];
};

const DEFAULT_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  analytics: false
};

const COOKIE_CLEANUP_KEYS = [
  '_ga',
  '_gid',
  '_gat',
  '_gat_gtag_UA_*',
  '_ga_*'
];

const getCookieDomainsToClear = () => {
  const hostname = window.location.hostname;
  const hostnameParts = hostname.split('.').filter(Boolean);
  const domains = new Set<string | undefined>([undefined]);

  if (!hostname || hostname === 'localhost') {
    return [...domains];
  }

  domains.add(hostname);
  domains.add(`.${hostname}`);

  for (let index = 1; index < hostnameParts.length - 1; index += 1) {
    const domainLevel = hostnameParts.slice(index).join('.');
    domains.add(domainLevel);
    domains.add(`.${domainLevel}`);
  }

  return [...domains];
};

const removeCookieEverywhere = (cookieName: string) => {
  getCookieDomainsToClear().forEach(domain => {
    Cookies.remove(cookieName, { path: '/' });

    if (domain) {
      Cookies.remove(cookieName, { path: '/', domain });
    }
  });
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

const FALLBACK_COOKIE_CONSENT_CONTEXT: CookieConsentContextValue = {
  consent: null,
  preferences: DEFAULT_PREFERENCES,
  shouldShowBanner: false,
  shouldShowConsentControls: false,
  isPreferencesOpen: false,
  openPreferences: () => {
    // noop
  },
  closePreferences: () => {
    // noop
  },
  acceptAll: () => {
    // noop
  },
  rejectAll: () => {
    // noop
  },
  savePreferences: () => {
    // noop
  },
  hasConsentFor: category => category === 'necessary',
  getAuditLog: () => []
};

const readStorage = <T,>(storageKey: string): T | null => {
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return rawValue ? (JSON.parse(rawValue) as T) : null;
  } catch {
    return null;
  }
};

const writeStorage = (storageKey: string, value: unknown) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // noop
  }
};

const isConsentExpired = (consent: CookieConsentRecord | null) => {
  if (!consent) {
    return true;
  }

  return new Date(consent.expiresAt).getTime() <= Date.now();
};

const resolveConsentStatus = (
  preferences: CookieConsentPreferences
): CookieConsentRecord['status'] =>
  preferences.analytics ? 'accepted' : 'rejected';

const clearNonEssentialCookies = () => {
  COOKIE_CLEANUP_KEYS.forEach(cookieName => {
    if (cookieName.includes('*')) {
      const prefix = cookieName.replace('*', '');
      Object.keys(Cookies.get()).forEach(existingCookieName => {
        if (existingCookieName.startsWith(prefix)) {
          removeCookieEverywhere(existingCookieName);
        }
      });
      return;
    }

    removeCookieEverywhere(cookieName);
  });
};

export const CookieConsentProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [consent, setConsent] = useState<CookieConsentRecord | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [hasLoadedStoredConsent, setHasLoadedStoredConsent] = useState(false);

  useEffect(() => {
    const storedConsent = readStorage<CookieConsentRecord>(
      COOKIE_CONSENT_STORAGE_KEY
    );
    if (storedConsent && !isConsentExpired(storedConsent)) {
      setConsent(storedConsent);
    }
    setHasLoadedStoredConsent(true);
  }, []);

  const shouldShowConsentControls = true;
  const shouldShowBanner = hasLoadedStoredConsent && isConsentExpired(consent);

  const persistConsent = useCallback(
    (
      nextPreferences: CookieConsentPreferences,
      consentType: CookieConsentType
    ) => {
      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + getConsentDurationDays() * 24 * 60 * 60 * 1000
      );
      const nextConsent: CookieConsentRecord = {
        version: 1,
        status: resolveConsentStatus(nextPreferences),
        consentType,
        preferences: nextPreferences,
        timestamp: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        region: 'global'
      };

      writeStorage(COOKIE_CONSENT_STORAGE_KEY, nextConsent);
      const currentAuditLog =
        readStorage<CookieConsentRecord[]>(COOKIE_CONSENT_AUDIT_STORAGE_KEY) ?? [];
      writeStorage(COOKIE_CONSENT_AUDIT_STORAGE_KEY, [
        ...currentAuditLog,
        nextConsent
      ]);

      if (!nextPreferences.analytics) {
        clearNonEssentialCookies();
      }

      setConsent(nextConsent);
      setIsPreferencesOpen(false);
    },
    []
  );

  const openPreferences = useCallback(() => {
    setIsPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setIsPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({ necessary: true, analytics: true }, 'accept_all');
  }, [persistConsent]);

  const rejectAll = useCallback(() => {
    persistConsent({ necessary: true, analytics: false }, 'reject_all');
  }, [persistConsent]);

  const savePreferences = useCallback(
    (preferences: Omit<CookieConsentPreferences, 'necessary'>) => {
      persistConsent(
        { necessary: true, ...preferences },
        'custom'
      );
    },
    [persistConsent]
  );

  const getAuditLog = useCallback(() => {
    return readStorage<CookieConsentRecord[]>(COOKIE_CONSENT_AUDIT_STORAGE_KEY) ?? [];
  }, []);

  const contextValue = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      preferences: consent?.preferences ?? DEFAULT_PREFERENCES,
      shouldShowBanner,
      shouldShowConsentControls,
      isPreferencesOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      rejectAll,
      savePreferences,
      hasConsentFor: category =>
        category === 'necessary'
          ? true
          : (consent?.preferences ?? DEFAULT_PREFERENCES)[category],
      getAuditLog
    }),
    [
      acceptAll,
      closePreferences,
      consent,
      getAuditLog,
      isPreferencesOpen,
      openPreferences,
      rejectAll,
      savePreferences,
      shouldShowBanner,
      shouldShowConsentControls
    ]
  );

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
      <CookieConsentBanner />
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);

  return context ?? FALLBACK_COOKIE_CONSENT_CONTEXT;
};
